import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import {
	CoreState,
	CovidMeasuresConfigService,
	DepositLimitConfigService,
	JurisdictionPrefixService,
	Jurisdiction,
	JurisdictionSelector,
	MultiDepositLimitAction,
} from "@obg/ngx.core";
import { coerceBoolean } from "@obg/ngx.global.evo";
import {
	MultiDepositFormValidatonService,
	MultiDepositLimitContainerService,
	MultiDepositLimitService,
} from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { Observable, merge, Subscription } from "rxjs";
import { map, take } from "rxjs/operators";

@Component({
	selector: "obg-m-multi-deposit-limit-container",
	templateUrl: "./multi-deposit-limit.container.html",
	styleUrls: ["./multi-deposit-limit.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	providers: [MultiDepositFormValidatonService, MultiDepositLimitContainerService, MultiDepositLimitService],
})
export class MultiDepositLimitContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-multi-deposit-limit") hostClass = true;

	@Input() set navigateToReturnUrl(value: boolean) {
		this._navigateToReturnUrl = coerceBooleanProperty(value);
		this.multiDepositService.init(this._navigateToReturnUrl);
	}

	@Input() @coerceBoolean() collapseDepositInfo = false;
	@Input() @coerceBoolean() newLoginFlow = false;
	@Input() @coerceBoolean() hideDepositLimitCalculatorBanner = false;

	@Output() navigate = new EventEmitter();

	readonly threshold = this.depositLimitConfig.getDepositLimitExceedMessageThreshold();

	private _navigateToReturnUrl = false;
	private readonly depositLimitExceedMessageEnabled =
		this.covidMeasuresConfig.covidMeasuresEnabled() &&
		this.depositLimitConfig.getDepositLimitExceedMessageEnabled();
	private data$$: Subscription | undefined;
	isJurisdictionSga$: Observable<boolean> | undefined;

	constructor(
		public multiDepositService: MultiDepositLimitContainerService,
		private store: Store<CoreState>,
		private multiDepositLimitAction: MultiDepositLimitAction,
		private changeDetector: ChangeDetectorRef,
		private depositLimitConfig: DepositLimitConfigService,
		private covidMeasuresConfig: CovidMeasuresConfigService,
		private jurisdictionPrefixService: JurisdictionPrefixService
	) {}

	ngOnInit() {
		this.store.dispatch(this.multiDepositLimitAction.get());

		const depositLimitUpdate$ = this.multiDepositService.depositLimitUpdate$;

		this.data$$ = merge(depositLimitUpdate$).subscribe(() => this.changeDetector.markForCheck());

		this.isJurisdictionSga$ = this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			map((jurisdiction) => jurisdiction === Jurisdiction.Sga)
		);
	}

	navigateToResponsibleGaming() {
		this.multiDepositService.navigateToResponsibleGaming();
		this.navigate.emit();
	}

	toggleInputFields(isInputsVisible: boolean) {
		this.multiDepositService.toggleInputsVisibility(isInputsVisible);
	}

	onSubmit(redirectToDeposit: boolean | undefined) {
		const weeklyDepositLimitValue = this.multiDepositService.getWeeklyDepositValue();
		this.jurisdictionPrefixService
			.translateByJurisdictionPrefix$(
				this.isCovidMessageVisible(weeklyDepositLimitValue)
					? "multi-deposit-limit.confirmation-with-covid19-caption"
					: "multi-deposit-limit.confirmation-caption",
				this.isCovidMessageVisible(weeklyDepositLimitValue) ? { threshold: this.threshold } : undefined
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe((message) =>
				this.multiDepositService.submitForm(redirectToDeposit || this.newLoginFlow, message)
			);
	}

	isCovidMessageVisible(weeklyDepositLimit: number): boolean {
		return this.depositLimitExceedMessageEnabled && weeklyDepositLimit > this.threshold;
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}
}
